import { Close, CloudUpload } from '@mui/icons-material';
import { Chip, FormControlLabel } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import Button from '../../components/formElements/Button';
import RichTextEditor from '../../components/global/RichTextEditor';
import { useParameter } from '../../context/ParameterContext';
import { useProduct } from '../../context/ProductContext';
import { Parameter } from '../../models/Parameter';
import { Product, ProductFormData, Tag } from '../../models/Product';
import CategorySelect from '../components/CategorySelect';
import ProducerSelect from '../components/ProducerSelect';
import PackageSectionForm from './package/PackageSectionForm';

interface ProductFormProps {
  product?: Product;
  inEditMode?: boolean;
}

function ProductForm({ product, inEditMode = false }: ProductFormProps) {
  const { createProduct, editProduct } = useProduct();

  const [parameters, setParameters] = useState<Parameter[]>([]);
  const { getParametersForCategory } = useParameter();

  const [categoryId, setCategoryId] = useState<number>(
    product?.categoryId || 0,
  );

  const [previewImages, setPreviewImages] = useState<string[]>([]);

  useEffect(() => {
    const fetchParameters = async () => {
      if (categoryId > 0) {
        const parameters = await getParametersForCategory(categoryId);
        setParameters(parameters);
      }
    };

    fetchParameters();
  }, [categoryId, getParametersForCategory]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<ProductFormData>({
    defaultValues: {
      name: product?.name || '',
      serialNumber: product?.serialNumber || '',
      categoryId: product?.categoryId || 0,
      producerId: product?.producerId || 0,
      description: product?.description || '',
      price: product?.price || 0,
      primaryImage: null,
      keywords: product?.keywords || '',
      secondaryImages: [] as File[],
      secondaryImagesOrders:
        product?.secondaryImages?.map((img) => img.order) || [],
      parameterValues: parameters.map((param) => ({
        id: param.id,
        value:
          product?.parameterValues?.find((pv) => pv.parameterId === param.id)
            ?.value || '',
      })),
      draft: product?.draft || false,
      tag: product?.tag || Tag.New,
      packaged: product?.packaged || false,
      packageDimensions: product?.packageDimensions || [0, 0, 0],
      packageWeight: product?.packageWeight || 0,
      singleItemDeliveryCost: product?.singleItemDeliveryCost || 0,
    },
  });

  const navigate = useNavigate();

  const [keywordInput, setKeywordInput] = useState('');
  const [keywordList, setKeywordList] = useState<string[]>(
    product?.keywords ? product.keywords.split(';') : [],
  );

  const [changeCategory, setChangeCategory] = useState(false);
  const [changeProducer, setChangeProducer] = useState(false);
  const [changePrimaryImage, setChangePrimaryImage] = useState(false);
  const [removedImages, setRemovedImages] = useState(new Set<number>());

  const [primaryImagePreview, setPrimaryImagePreview] = useState<string | null>(
    product?.primaryImage || null,
  );

  const handleKeywordKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      if (keywordInput.trim()) {
        setKeywordList([...keywordList, keywordInput.trim()]);
        setKeywordInput('');
      }
    }
  };

  const removeKeyword = (index: number) => {
    setKeywordList(keywordList.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setValue('keywords', keywordList.join(';'));
  }, [keywordList, setValue]);

  useEffect(() => {
    if (product?.primaryImage) setValue('primaryImage', null);
  }, [product?.primaryImage, setValue]);

  useEffect(() => {
    setValue(
      'parameterValues',
      parameters.map((param) => ({
        id: param.id,
        value:
          product?.parameterValues?.find((pv) => pv.parameterId === param.id)
            ?.value || '',
      })),
    );
  }, [product?.parameterValues, parameters, setValue]);

  // Obsługa podglądu zdjęć
  useEffect(() => {
    if (product?.secondaryImages && product.secondaryImages.length > 0) {
      const urls = product.secondaryImages.map((img) => img.image);
      setPreviewImages(urls);
    }
  }, [product?.secondaryImages]);

  const handlePrimaryImageChange = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrimaryImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setValue('primaryImage', file);
    } else {
      setPrimaryImagePreview(product?.primaryImage || null);
      setValue('primaryImage', null);
    }
  };

  const handleSecondaryImagesAdd = (files: FileList | null) => {
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      const currentSecondaryImages = getValues('secondaryImages') || [];
      const newOrder = currentSecondaryImages.length;

      // Tworzenie podglądów
      filesArray.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImages((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });

      // Aktualizacja formularza
      setValue('secondaryImages', [...currentSecondaryImages, ...filesArray]);
      setValue('secondaryImagesOrders', [
        ...getValues('secondaryImagesOrders'),
        ...Array(filesArray.length)
          .fill(0)
          .map((_, i) => newOrder + i),
      ]);
    }
  };

  const onSubmit = async (data: ProductFormData) => {
    const toastId = toast.loading(
      inEditMode ? 'Edytowanie...' : 'Dodawanie...',
      {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
        theme: 'colored',
      },
    );

    try {
      if (inEditMode && product?.id) {
        const formData = new FormData();
        // Prawidłowo dodajemy primaryImage i secondaryImages do formData
        if (data.primaryImage instanceof File) {
          formData.append('primaryImage', data.primaryImage);
        }
        
        if (Array.isArray(data.secondaryImages)) {
          data.secondaryImages.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`secondaryImages[${index}]`, file);
            }
          });
        }
        
        // Dodajemy pozostałe pola formularza
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'parameterValues' && Array.isArray(value)) {
            value.forEach((param, index) => {
              if (param.id && param.value !== undefined) {
                formData.append(`parameterValues[${index}].id`, param.id.toString());
                formData.append(`parameterValues[${index}].value`, param.value.toString());
              }
            });
          } else if (key === 'secondaryImagesOrders' && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (key !== 'primaryImage' && key !== 'secondaryImages' && value !== null && value !== undefined) {
            formData.append(key, value.toString());
          }
        });
        
        await editProduct(product?.id, Array.from(removedImages), formData);
        toast.update(toastId, {
          render: 'Produkt edytowany!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        const formData = new FormData();
        // Prawidłowo dodajemy primaryImage i secondaryImages do formData
        if (data.primaryImage instanceof File) {
          formData.append('primaryImage', data.primaryImage);
        }
        
        if (Array.isArray(data.secondaryImages)) {
          data.secondaryImages.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`secondaryImages[${index}]`, file);
            }
          });
        }
        
        // Dodajemy pozostałe pola formularza
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'parameterValues' && Array.isArray(value)) {
            value.forEach((param, index) => {
              if (param.id && param.value !== undefined) {
                formData.append(`parameterValues[${index}].id`, param.id.toString());
                formData.append(`parameterValues[${index}].value`, param.value.toString());
              }
            });
          } else if (key === 'secondaryImagesOrders' && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (key !== 'primaryImage' && key !== 'secondaryImages' && value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach((item) => formData.append(key, item));
            } else {
              formData.append(key, value.toString());
            }
          }
        });
        
        await createProduct(formData);
        toast.update(toastId, {
          render: 'Produkt dodany!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }

      navigate('/admin/produkty');
    } catch (error: any) {
      console.error('Failed to process product', error);
      toast.update(toastId, {
        render: inEditMode
          ? 'Nie udało się edytować produktu'
          : 'Nie udało się dodać produktu',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const removeImage = (index: number) => {
    if (product?.secondaryImages?.[index]?.id) {
      setRemovedImages(
        new Set([...removedImages, product.secondaryImages[index].id]),
      );
    }

    setPreviewImages((prev) => prev.filter((_, i) => i !== index));

    const currentImages = getValues('secondaryImages');
    const currentOrders = getValues('secondaryImagesOrders');

    setValue(
      'secondaryImages',
      currentImages.filter((_, i) => i !== index),
    );
    setValue(
      'secondaryImagesOrders',
      currentOrders.filter((_, i) => i !== index),
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white rounded-lg shadow-md p-6"
    >
      <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-200">
        {inEditMode ? 'Edycja produktu' : 'Dodaj nowy produkt'}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pierwsza kolumna - Zdjęcia */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            <h3 className="font-medium text-gray-900 mb-3">Zdjęcia produktu</h3>

            {/* Zdjęcie główne */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zdjęcie główne
              </label>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                <Controller
                  name="primaryImage"
                  control={control}
                  render={({ field }) => (
                    <>
                      <input
                        id="primary-image-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handlePrimaryImageChange(e.target.files?.[0] || null)
                        }
                        className="hidden"
                      />

                      <label
                        htmlFor="primary-image-input"
                        className="cursor-pointer w-full"
                      >
                        {primaryImagePreview ? (
                          <div className="relative">
                            <img
                              src={primaryImagePreview}
                              alt="Zdjęcie główne"
                              className="mx-auto max-h-48 object-contain"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                              <span className="text-white opacity-0 hover:opacity-100 bg-black bg-opacity-50 px-3 py-1 rounded">
                                Zmień zdjęcie
                              </span>
                            </div>
                            {inEditMode && !changePrimaryImage && (
                              <Button
                                type="button"
                                colorTheme="error"
                                size="xs"
                                className="absolute top-0 right-0 w-auto"
                                onClick={() => {
                                  setChangePrimaryImage(true);
                                  setPrimaryImagePreview(null);
                                }}
                              >
                                <Close fontSize="small" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <CloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">
                              Kliknij aby dodać główne zdjęcie produktu
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              PNG, JPG, GIF do 5MB
                            </p>
                          </div>
                        )}
                      </label>
                    </>
                  )}
                />
              </div>
            </div>

            {/* Zdjęcia dodatkowe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zdjęcia dodatkowe
              </label>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4">
                <input
                  type="file"
                  id="secondary-images-input"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleSecondaryImagesAdd(e.target.files)}
                />

                <label
                  htmlFor="secondary-images-input"
                  className="cursor-pointer block text-center py-4"
                >
                  <CloudUpload className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-1 text-sm text-gray-500">
                    Dodaj zdjęcia dodatkowe
                  </p>
                </label>
              </div>

              {previewImages.length > 0 && (
                <div className="mt-4">
                  <Swiper
                    modules={[Navigation, Pagination]}
                    spaceBetween={10}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    className="rounded-lg overflow-hidden"
                  >
                    {previewImages.map((src, index) => (
                      <SwiperSlide key={index}>
                        <div className="relative">
                          <img
                            src={src}
                            alt={`Zdjęcie ${index + 1}`}
                            className="w-full h-48 object-contain"
                          />
                          <Button
                            type="button"
                            colorTheme="error"
                            size="xs"
                            className="absolute top-2 right-2 w-auto"
                            onClick={() => removeImage(index)}
                          >
                            <Close fontSize="small" />
                          </Button>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Druga kolumna - Dane podstawowe */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <h3 className="font-medium text-gray-900 mb-3">
                Informacje podstawowe
              </h3>
            </div>

            <div className="md:col-span-2">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nazwa produktu *
              </label>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Nazwa jest wymagana' }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="name"
                    className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                    placeholder="Wpisz nazwę produktu"
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">
                  {String(errors.name.message)}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="serialNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Numer seryjny *
              </label>
              <Controller
                name="serialNumber"
                control={control}
                rules={{ required: 'Numer seryjny jest wymagany' }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="serialNumber"
                    className={`w-full px-3 py-2 border ${errors.serialNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                    placeholder="np. ABC-12345"
                  />
                )}
              />
              {errors.serialNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {String(errors.serialNumber.message)}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Cena *
              </label>
              <Controller
                name="price"
                control={control}
                rules={{ required: 'Cena jest wymagana', min: 0 }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="price"
                    type="number"
                    step="0.01"
                    className={`w-full px-3 py-2 border ${errors.price ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                    placeholder="0.00"
                  />
                )}
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">
                  {String(errors.price.message)}
                </p>
              )}
            </div>

            {inEditMode && !changeCategory && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategoria
                </label>
                <div className="flex items-center space-x-2 p-2 border border-gray-300 rounded-md">
                  <p>
                    {product?.category
                      ? product.category.name
                      : 'Nie wybrano kategorii'}
                  </p>
                  <Button
                    type="button"
                    colorTheme="outline"
                    size="xs"
                    className="ml-auto w-auto"
                    onClick={() => setChangeCategory(true)}
                  >
                    Zmień
                  </Button>
                </div>
              </div>
            )}

            {(!inEditMode || changeCategory) && (
              <div className="md:col-span-2">
                <CategorySelect
                  setCategoryId={setCategoryId}
                  control={control}
                  errors={errors}
                />
              </div>
            )}

            {inEditMode && !changeProducer && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Producent
                </label>
                <div className="flex items-center space-x-2 p-2 border border-gray-300 rounded-md">
                  <p>
                    {product?.producer
                      ? product.producer.name
                      : 'Nie wybrano producenta'}
                  </p>
                  <Button
                    type="button"
                    colorTheme="outline"
                    size="xs"
                    className="ml-auto w-auto"
                    onClick={() => setChangeProducer(true)}
                  >
                    Zmień
                  </Button>
                </div>
              </div>
            )}

            {(!inEditMode || changeProducer) && (
              <div className="md:col-span-2">
                <ProducerSelect control={control} errors={errors} />
              </div>
            )}

            <div>
              <label
                htmlFor="tag"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tag produktu
              </label>
              <Controller
                name="tag"
                control={control}
                render={({ field }) => (
                  <select
                    id="tag"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    value={field.value?.toString() || ''}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value ? parseInt(e.target.value, 10) : null,
                      );
                    }}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    name={field.name}
                  >
                    <option value="">Brak</option>
                    <option value={Tag.New.toString()}>Nowy</option>
                    <option value={Tag.Sale.toString()}>Wyprzedaż</option>
                    <option value={Tag.Bestseller.toString()}>
                      Bestseller
                    </option>
                    <option value={Tag.Recommended.toString()}>Polecany</option>
                  </select>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormControlLabel
                control={
                  <Controller
                    name="draft"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    )}
                  />
                }
                label="Zapisz jako wersję roboczą"
                className="mr-0"
              />
            </div>

            {/* Słowa kluczowe */}
            <div className="md:col-span-2">
              <label
                htmlFor="keywords-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Słowa kluczowe
              </label>
              <div className="flex">
                <input
                  id="keywords-input"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={handleKeywordKeyDown}
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Wpisz słowo kluczowe i naciśnij Enter"
                />
                <Button
                  type="button"
                  colorTheme="outline"
                  onClick={() => {
                    if (keywordInput.trim()) {
                      setKeywordList([...keywordList, keywordInput.trim()]);
                      setKeywordInput('');
                    }
                  }}
                  className="ml-2 w-auto"
                >
                  Dodaj
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                {keywordList.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    onDelete={() => removeKeyword(index)}
                    className="m-1"
                  />
                ))}
              </div>
            </div>

            <div className="md:col-span-2 mt-4">
              <h3 className="font-medium text-gray-900 mb-2">
                Opakowanie i dostawa
              </h3>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <p className="text-sm text-gray-500 mb-4">
                  Jeśli produkt jest zapakowany w wymiarowe opakowanie, podaj
                  wymiary i wagę opakowania, a koszt wysyłki zostanie obliczony
                  automatycznie. W przeciwnym wypadku podaj koszt dostawy
                  pojedynczego produktu.
                </p>
                <PackageSectionForm control={control} errors={errors} />
              </div>
            </div>

            <div className="md:col-span-2 mt-4">
              <h3 className="font-medium text-gray-900 mb-2">Opis produktu</h3>
              <RichTextEditor
                name="description"
                control={control}
                defaultValue={product?.description || ''}
              />
            </div>
          </div>

          {/* Parametry produktu */}
          {parameters.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-3">
                Parametry produktu
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {parameters.map((param, index) => (
                  <Controller
                    key={param.id}
                    name={`parameterValues.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label
                          htmlFor={`param-${param.id}`}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {param.name}
                        </label>
                        <input
                          {...field}
                          id={`param-${param.id}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setValue(`parameterValues.${index}.id`, param.id);
                          }}
                        />
                      </div>
                    )}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Przyciski formularza */}
      <div className="mt-8 flex justify-end space-x-4">
        <Button
          type="button"
          colorTheme="outline"
          onClick={() => navigate('/admin/produkty')}
          className="w-auto px-6"
        >
          Anuluj
        </Button>
        <Button type="submit" colorTheme="primary" className="w-auto px-6">
          {inEditMode ? 'Zaktualizuj produkt' : 'Utwórz produkt'}
        </Button>
      </div>
    </form>
  );
}

export default ProductForm;
